import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Job } from "@domain/CRM/job";
import { UserContext } from "@gui/contexts/user/UserContext";
import { routePaths } from "@gui/routes/routeTable";

/** The `/jobs/…` pages that are not the Job Detail page. */
const OTHER_JOBS_PAGES = new Set(["dashboard", "submit", "ready"]);

/** The job the Job Detail page shows: its routed id, `""` on bare `/jobs`, or null when the page isn't Job Detail. */
function jobDetailRouteJobId(pathname: string): string | null {
	const match = /^\/jobs(?:\/([^/]+))?\/?$/.exec(pathname);
	if (!match) return null;
	const segment = match[1];
	if (!segment) return "";
	return OTHER_JOBS_PAGES.has(segment) ? null : decodeURIComponent(segment);
}

/** The query a job opened from this page takes: Job Detail's own, or from elsewhere the admin's `?company=` alone,
 *  because the rest of the query belongs to the page being left. */
function queryForJobDetail(search: string, onJobDetail: boolean): string {
	if (onJobDetail) return search;
	const company = new URLSearchParams(search).get("company");
	return company ? `?${new URLSearchParams({ company })}` : "";
}

const isSearchShortcut = (event: KeyboardEvent) =>
	(event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === "k";

/**
 * The toolbar's job search, for admins on every page. A job it finds is opened by putting its id in the URL — the page
 * loads whatever the route names, so the search never selects a job itself. The box follows the route, so it names the
 * job on screen and is empty elsewhere.
 *
 * @param onDone Called once a job is chosen. A pick that changes the route closes the toolbar's menus anyway; a pick of
 * the job already on screen does not.
 */
export function useToolbarJobSearchViewModel(onDone?: () => void) {
	const user = useContext(UserContext);
	const { pathname, search } = useLocation();
	const navigate = useNavigate();
	const routeJobId = jobDetailRouteJobId(pathname);
	const [value, setValue] = useState(routeJobId ?? "");
	const inputRef = useRef<HTMLInputElement>(null);
	const visible = Boolean(user?.isAdmin);

	useEffect(() => {
		setValue(routeJobId ?? "");
	}, [pathname, routeJobId]);

	useEffect(() => {
		if (!visible) return;
		const focusOnShortcut = (event: KeyboardEvent) => {
			if (!isSearchShortcut(event)) return;
			const input = inputRef.current;
			// An open modal hides the app from assistive tech, and keeps the key: focus stays in the dialog.
			if (!input || input.closest('[aria-hidden="true"]')) return;
			event.preventDefault();
			input.focus();
			input.select();
		};
		window.addEventListener("keydown", focusOnShortcut, { capture: true });
		return () => window.removeEventListener("keydown", focusOnShortcut, { capture: true });
	}, [visible]);

	const onJobIdChosen = (id: string) => {
		if (id === routeJobId) setValue(id);
		else navigate({ pathname: routePaths.jobDetail(id), search: queryForJobDetail(search, routeJobId !== null) });
		onDone?.();
	};

	const onFound = (job: Job) => onJobIdChosen(job.id);

	const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);

	return {
		visible,
		value,
		setValue,
		onFound,
		onJobIdChosen,
		inputRef,
		// A new field per page: a miss's message and any work in flight belong to the page they started on.
		fieldKey: pathname,
		placeholder: `Search jobs (${isMac ? "⌘K" : "Ctrl+K"})`,
	};
}

export type ToolbarJobSearchVM = ReturnType<typeof useToolbarJobSearchViewModel>;
